import React, { useState } from 'react';
import { User } from '../services/groupService';
import { transferService } from '../services/transferService';
import UserAutocomplete from './UserAutocomplete';

interface TransferCustomerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onTransferComplete: () => void;
  customerId: string;
  customerName: string;
}

const TransferCustomerDialog: React.FC<TransferCustomerDialogProps> = ({
  isOpen,
  onClose,
  onTransferComplete,
  customerId,
  customerName,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [keepAccess, setKeepAccess] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState('');

  const selectedUser = selectedUsers.length > 0 ? selectedUsers[0] : null;
  const isConfirmed = confirmText.toLowerCase() === customerName.toLowerCase();
  const canTransfer = selectedUser && isConfirmed && !transferring;

  const handleTransfer = async () => {
    if (!selectedUser || !isConfirmed) return;

    setTransferring(true);
    setError('');
    try {
      await transferService.transferCustomer(customerId, selectedUser.id, keepAccess);
      onTransferComplete();
      handleClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to transfer customer');
    } finally {
      setTransferring(false);
    }
  };

  const handleClose = () => {
    setSelectedUsers([]);
    setKeepAccess(true);
    setConfirmText('');
    setError('');
    onClose();
  };

  // Only allow selecting one user at a time
  const handleUserSelect = (users: User[]) => {
    if (users.length > 1) {
      setSelectedUsers([users[users.length - 1]]);
    } else {
      setSelectedUsers(users);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75" onClick={handleClose} />

        {/* Modal */}
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Transfer Customer
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {customerName}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Warning Banner */}
          <div className="mx-6 mt-4 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                This will <strong>permanently transfer ownership</strong> of this customer and all associated data (projects, documents, reports, workflows) to the selected user.
              </p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mt-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* User selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Transfer to
              </label>
              <UserAutocomplete
                selectedUsers={selectedUsers}
                onSelect={handleUserSelect}
                placeholder="Search for a user by name or email..."
              />
            </div>

            {/* Keep access checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={keepAccess}
                onChange={(e) => setKeepAccess(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Keep read-only access after transfer
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  You'll still be able to view this customer's data as a shared customer
                </p>
              </div>
            </label>

            {/* Confirmation */}
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type <span className="font-semibold text-gray-900 dark:text-white">"{customerName}"</span> to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={customerName}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleTransfer}
              disabled={!canTransfer}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {transferring ? 'Transferring...' : 'Transfer Ownership'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferCustomerDialog;
